import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Stop,
  PlayArrow as StartIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';

import {
  startSession,
  endSession,
  getSessionStatus,
  submitSessionFeedback
} from '../Services/sessionService';
import { checkClient } from '../Services/leadsService';
import Media from '../Components/Media/Media';
import SessionFeedbackModal from '../Components/SessionFeedbackModal';
import { getBrowserLocation, sanitizeLocationPayload } from '../utils/location';
import { reverseGeocode } from '../Services/locationService';





const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: '#f8f9fa',
  padding: theme.spacing(4, 2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, 4),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(70, 80, 100, 0.1)',
  overflow: 'hidden',
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
}));

const DEFAULT_MEETING_LOCATION_ADDRESS = 'Meeting Room';
const MEETING_LOCATION_NOTES = 'Meeting session';



const Meeting = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [validating, setValidating] = useState(false);

  // Country codes for phone numbers
  const countryCodes = [
    { code: '+20', name: 'Egypt', flag: '🇪🇬' },
    { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+971', name: 'UAE', flag: '🇦🇪' },
    { code: '+965', name: 'Kuwait', flag: '🇰🇼' },
    { code: '+974', name: 'Qatar', flag: '🇶🇦' },
    { code: '+973', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+968', name: 'Oman', flag: '🇴🇲' },
    { code: '+962', name: 'Jordan', flag: '🇯🇴' },
    { code: '+961', name: 'Lebanon', flag: '🇱🇧' },
    { code: '+1', name: 'USA/Canada', flag: '🇺🇸' },
    { code: '+44', name: 'UK', flag: '🇬🇧' },
    { code: '+49', name: 'Germany', flag: '🇩🇪' },
    { code: '+33', name: 'France', flag: '🇫🇷' }
  ];

  // Session management state
  const [sessionState, setSessionState] = useState({
    hasActiveSession: false,
    activeSession: null,
    loading: false
  });
  const [startSessionDialog, setStartSessionDialog] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhoneNumber: '',
    countryCode: '+20' // Default to Egypt
  });

  const [formErrors, setFormErrors] = useState({
    clientName: '',
    clientEmail: '',
    clientPhoneNumber: ''
  });

  const [feedbackModal, setFeedbackModal] = useState({
    open: false,
    sessionData: null,
    loading: false
  });

  useEffect(() => {
    checkSessionStatus();
  }, []);

  const captureLocationDetails = useCallback(async (label, actionVerb) => {
    const locationResult = await getBrowserLocation({ locationAddress: label });

    if (locationResult.error || typeof locationResult.latitude !== 'number' || typeof locationResult.longitude !== 'number') {
      throw new Error(`Unable to ${actionVerb} the session without location access. Please enable location permissions and try again.`);
    }

    try {
      const resolvedAddress = await reverseGeocode(locationResult.latitude, locationResult.longitude);
      return sanitizeLocationPayload({
        latitude: locationResult.latitude,
        longitude: locationResult.longitude,
        locationAddress: resolvedAddress
      });
    } catch (geocodeError) {
      console.error('Reverse geocoding failed:', geocodeError);
      throw new Error(`Unable to ${actionVerb} the session because we could not determine the location address. Please try again.`);
    }
  }, []);

  const checkSessionStatus = async () => {
    try {
      setSessionState(prev => ({ ...prev, loading: true }));
      const response = await getSessionStatus();
      
      if (response.succeeded) {
        const hasActiveMeeting = response.data.hasActiveMeeting;
        setSessionState({
          hasActiveSession: hasActiveMeeting,
          activeSession: hasActiveMeeting ? response.data.activeMeetingSession : null,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error checking session status:', error);
      setSessionState(prev => ({ ...prev, loading: false }));
    }
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'clientName':
        if (!value.trim()) error = 'Client name is required';
        else if (value.trim().length < 2) error = 'Name must be at least 2 characters';
        break;
      case 'clientEmail':
        if (value.trim()) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) error = 'Please enter a valid email address';
        }
        break;
      case 'clientPhoneNumber':
        if (value.trim()) {
          const phoneRegex = /^\d{7,15}$/;
          if (!phoneRegex.test(value.replace(/\s+/g, ''))) error = 'Phone number must be 7-15 digits';
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  const validateAllFields = () => {
    const errors = {
      clientName: validateField('clientName', sessionForm.clientName),
      clientEmail: validateField('clientEmail', sessionForm.clientEmail),
      clientPhoneNumber: validateField('clientPhoneNumber', sessionForm.clientPhoneNumber)
    };
    
    setFormErrors(errors);
    return Object.values(errors).every(error => !error);
  };

  const handleStartSession = async () => {
    if (!validateAllFields()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors before starting the session.',
        severity: 'error'
      });
      return;
    }

    // Validate client is not assigned to another sales agent
    try {
      setValidating(true);
      const fullPhone = sessionForm.clientPhoneNumber
        ? sessionForm.countryCode + sessionForm.clientPhoneNumber
        : undefined;
      const check = await checkClient({
        email: sessionForm.clientEmail || undefined,
        phoneNumber: fullPhone,
      });
      if (check?.isOtherSalesLead) {
        setSnackbar({
          open: true,
          message: 'This client is already assigned to another sales agent.',
          severity: 'error'
        });
        setValidating(false);
        return;
      }
    } catch {
      // If check fails, proceed (non-blocking)
    } finally {
      setValidating(false);
    }

    try {
      setSessionState(prev => ({ ...prev, loading: true }));

      const defaultLabel = DEFAULT_MEETING_LOCATION_ADDRESS;
      const locationDetails = await captureLocationDetails(`${defaultLabel} (Start)`, 'start');
      const locationLabel = locationDetails.locationAddress || defaultLabel;

      const fullPhone = sessionForm.clientPhoneNumber
        ? sessionForm.countryCode + sessionForm.clientPhoneNumber
        : '';

      const sessionData = {
        sessionType: 'Meeting',
        clientName: sessionForm.clientName,
        clientEmail: sessionForm.clientEmail,
        clientPhoneNumber: fullPhone,
        location: locationLabel,
        notes: `${MEETING_LOCATION_NOTES} started`,
        ...locationDetails
      };

      const response = await startSession(sessionData);

      if (response.succeeded) {
        setSessionState({
          hasActiveSession: true,
          activeSession: response.data,
          loading: false
        });
        setStartSessionDialog(false);
        setSnackbar({
          open: true,
          message: 'Meeting session started successfully!',
          severity: 'success'
        });
      } else {
        setSessionState(prev => ({ ...prev, loading: false }));
        setSnackbar({
          open: true,
          message: response.message || 'Failed to start session',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error starting session:', error);
      setSessionState(prev => ({ ...prev, loading: false }));
      setSnackbar({
        open: true,
        message: error.message || 'Failed to start session',
        severity: 'error'
      });
    }
  };

  const handleEndSession = async () => {
    try {
      setSessionState(prev => ({ ...prev, loading: true }));

      if (!sessionState.activeSession?.id) {
        throw new Error('No active meeting session to end.');
      }

      const defaultLabel = sessionState.activeSession?.locationAddress || DEFAULT_MEETING_LOCATION_ADDRESS;
      const locationDetails = await captureLocationDetails(`${defaultLabel} (End)`, 'end');
      const locationLabel = locationDetails.locationAddress || defaultLabel;
      
      const response = await endSession(
        sessionState.activeSession.id,
        'Session ended from meeting page',
        locationDetails
      );
      
      if (response.succeeded) {
        // Show feedback modal after ending session
        setFeedbackModal({
          open: true,
          sessionData: {
            ...sessionState.activeSession,
            ...locationDetails,
            locationAddress: locationLabel,
            endTime: new Date().toISOString(),
            duration: response.data?.duration || 'Completed'
          },
          loading: false
        });
        
        setSessionState({
          hasActiveSession: false,
          activeSession: null,
          loading: false
        });
      } else {
        setSessionState(prev => ({ ...prev, loading: false }));
        setSnackbar({
          open: true,
          message: response.message || 'Failed to end session',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error ending session:', error);
      setSessionState(prev => ({ ...prev, loading: false }));
      setSnackbar({
        open: true,
        message: error.message || 'Failed to end session',
        severity: 'error'
      });
    }
  };

  const handleFeedbackSubmit = async (feedbackData) => {
    try {
      setFeedbackModal(prev => ({ ...prev, loading: true }));
      
      const response = await submitSessionFeedback(
        feedbackModal.sessionData.id, 
        feedbackData
      );
      
      if (response.succeeded) {
        setFeedbackModal({ open: false, sessionData: null, loading: false });
        setSnackbar({
          open: true,
          message: 'Meeting session ended and feedback submitted successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setFeedbackModal(prev => ({ ...prev, loading: false }));
      setSnackbar({
        open: true,
        message: error.message || 'Failed to submit feedback',
        severity: 'error'
      });
    }
  };

  const handleFeedbackModalClose = () => {
    setFeedbackModal({ open: false, sessionData: null, loading: false });
    setSnackbar({
      open: true,
      message: 'Meeting session ended successfully!',
      severity: 'success'
    });
  };

  const handleSessionInputChange = (e) => {
    const { name, value } = e.target;
    setSessionForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate on change
    const error = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleResetForm = () => {
    setSessionForm({
      clientName: '',
      clientEmail: '',
      clientPhoneNumber: '',
      countryCode: '+20'
    });
    setFormErrors({
      clientName: '',
      clientEmail: '',
      clientPhoneNumber: ''
    });
  };



  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };





  return (
    <StyledContainer maxWidth="lg">
      <StyledPaper elevation={0}>
        {/* Header */}
        <Box sx={{
          background: '#465064',
          color: 'white',
          p: 3,
          mb: 3,
          borderRadius: 2,
          position: 'relative'
        }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 600, 
            mb: 1,
            fontSize: { xs: '1.8rem', md: '2.2rem' }
          }}>
            🤝 Meeting Details
          </Typography>
                <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400, mb: 1 }}>
            Complete your meeting information and unit selection
          </Typography>
                {sessionState.hasActiveSession && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: { xs: 'center', md: 'flex-start' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                      <Chip 
                        label="Session Active" 
                        size="small" 
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.2)', 
                          color: 'white',
                          fontWeight: 500
                        }} 
                      />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Client: {sessionState.activeSession?.clientName}
                      </Typography>
                    </Box>
                    {sessionState.activeSession?.locationAddress && (
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Location: {sessionState.activeSession.locationAddress}
                        {typeof sessionState.activeSession.latitude === 'number' && typeof sessionState.activeSession.longitude === 'number'
                          ? ` (${sessionState.activeSession.latitude.toFixed(4)}, ${sessionState.activeSession.longitude.toFixed(4)})`
                          : ''}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: 2
              }}>
                {sessionState.loading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : sessionState.hasActiveSession ? (
                  <Button
                    variant="contained"
                    startIcon={<Stop />}
                    onClick={handleEndSession}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 500,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    End Session
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<StartIcon />}
                    onClick={() => setStartSessionDialog(true)}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 500,
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Start Session
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Media showQRCode={false} view={!sessionState.hasActiveSession} useSameTabForUnitPayment={true} />
        {/* <StyledStepper activeStep={activeStep}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel 
                StepIconComponent={() => (
                  <Avatar sx={{ 
                    background: activeStep >= index ? step.color : '#e9ecef',
                    color: 'white',
                    width: 40,
                    height: 40
                  }}>
                    {step.icon}
                  </Avatar>
                )}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </StyledStepper> */}

        {/* <Box component="form" onSubmit={handleSubmit}>
          {loading && activeStep === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress size={60} sx={{ color: '#667eea' }} />
            </Box>
          ) : (
            renderStepContent(activeStep)
          )}
          
          {activeStep !== 3 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 4,
              pt: 3,
              borderTop: '2px solid #e9ecef'
            }}>
              <StyledButton
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                variant="outlined"
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#5a6fd8',
                    background: 'rgba(102, 126, 234, 0.1)',
                  }
                }}
              >
                Back
              </StyledButton>
              <StyledButton
                variant="contained"
                onClick={handleNext}
                disabled={loading || (activeStep === 2 && !formData.selectedUnit)}
                endIcon={<ArrowForward />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  },
                  '&:disabled': {
                    background: '#e9ecef',
                    color: '#6c757d',
                  }
                }}
              >
                Next Step
              </StyledButton>
            </Box>
          )}
        </Box> */}
      </StyledPaper>

      {/* Start Session Dialog */}
      <Dialog
        open={startSessionDialog}
        onClose={() => !sessionState.loading && !validating && setStartSessionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #465064 0%, #2d3748 100%)',
          color: 'white',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
          <Box sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <HandshakeIcon sx={{ fontSize: '1.4rem' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Start Meeting Session
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Enter client information to begin
            </Typography>
          </Box>
        </Box>

        {/* Form body */}
        <DialogContent sx={{ px: 3, pt: 3, pb: 2, background: '#fff' }}>
          <Grid container spacing={2}>

            {/* Client Name */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#465064', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PersonIcon sx={{ fontSize: '0.95rem' }} /> Client Name <Box component="span" sx={{ color: '#e53935' }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                name="clientName"
                value={sessionForm.clientName}
                onChange={handleSessionInputChange}
                disabled={sessionState.loading || validating}
                placeholder="Full name"
                variant="outlined"
                size="small"
                error={!!formErrors.clientName}
                helperText={formErrors.clientName}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#465064' },
                    '&.Mui-focused fieldset': { borderColor: '#465064' },
                  },
                }}
              />
            </Grid>

            {/* Client Email */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#465064', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <EmailIcon sx={{ fontSize: '0.95rem' }} /> Email Address
              </Typography>
              <TextField
                fullWidth
                name="clientEmail"
                type="email"
                value={sessionForm.clientEmail}
                onChange={handleSessionInputChange}
                disabled={sessionState.loading || validating}
                placeholder="client@email.com"
                variant="outlined"
                size="small"
                error={!!formErrors.clientEmail}
                helperText={formErrors.clientEmail}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#465064' },
                    '&.Mui-focused fieldset': { borderColor: '#465064' },
                  },
                }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#465064', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PhoneIcon sx={{ fontSize: '0.95rem' }} /> Phone Number
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={sessionForm.countryCode}
                    name="countryCode"
                    onChange={handleSessionInputChange}
                    disabled={sessionState.loading || validating}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#465064' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#465064' },
                    }}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <span>{country.flag}</span>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{country.code}</Typography>
                          <Typography variant="caption" color="text.secondary">{country.name}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  name="clientPhoneNumber"
                  value={sessionForm.clientPhoneNumber}
                  onChange={handleSessionInputChange}
                  disabled={sessionState.loading || validating}
                  placeholder="Phone number"
                  variant="outlined"
                  size="small"
                  error={!!formErrors.clientPhoneNumber}
                  helperText={formErrors.clientPhoneNumber || `Full: ${sessionForm.countryCode}${sessionForm.clientPhoneNumber || 'XXXXXXXXX'}`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': { borderColor: '#465064' },
                      '&.Mui-focused fieldset': { borderColor: '#465064' },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        {/* Footer */}
        <Box sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #eee',
          background: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Button
            onClick={handleResetForm}
            disabled={sessionState.loading || validating}
            size="small"
            sx={{ color: '#e53935', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'rgba(229,57,53,0.08)' } }}
          >
            Reset
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={() => setStartSessionDialog(false)}
              disabled={sessionState.loading || validating}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 2,
                borderColor: '#ccc',
                color: '#555',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { borderColor: '#465064', color: '#465064', background: 'rgba(70,80,100,0.05)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartSession}
              disabled={sessionState.loading || validating || !sessionForm.clientName}
              variant="contained"
              size="small"
              startIcon={(sessionState.loading || validating) ? <CircularProgress size={14} color="inherit" /> : <StartIcon />}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #465064 0%, #2d3748 100%)',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                boxShadow: '0 3px 10px rgba(70,80,100,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #3a4354 0%, #242e3d 100%)', boxShadow: '0 4px 14px rgba(70,80,100,0.4)' },
                '&:disabled': { background: '#e0e0e0', color: '#9e9e9e', boxShadow: 'none' },
              }}
            >
              {validating ? 'Checking...' : sessionState.loading ? 'Starting...' : 'Start Session'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Session Feedback Modal */}
      <SessionFeedbackModal
        open={feedbackModal.open}
        onClose={handleFeedbackModalClose}
        onSubmit={handleFeedbackSubmit}
        sessionData={feedbackModal.sessionData}
        loading={feedbackModal.loading}
      />
    </StyledContainer>
  );
};

export default Meeting; 